// ============================================================================
// sim.js — one fixed-timestep logic tick, in order:
//   rebuild spatial hash → player → crowd AI → shockwaves → Titanfall → spawns
//   → warlord engagement → combo decay → outcome check.
// Hit-stop is handled by the caller (main) by simply not calling step() while
// world.hitstop is being drained, which freezes the whole simulation on impact.
// ============================================================================

import { TICK_DT, HIT_COUNTER_DECAY, UNIT_WARLORD, MORALE_DRIFT, MORALE_START } from './constants.js';
import { updatePlayer } from '../combat/player.js';
import { updateCrowd } from '../ai/crowd_ai.js';
import { updateShockwaves, updateTitanfall } from '../combat/titan.js';
import { updateSpawns } from '../world/spawns.js';
import { updateWarlordEngagement } from '../ai/warlords.js';
import { updateObjectives } from '../game/objectives.js';
import { updateLevelScript } from '../game/levelScript.js';
import { EV } from './events.js';

export class Sim {
  constructor(world) {
    this.world = world;
    // officer deaths drive banners/morale/counters; the OUTCOME is decided by
    // objectives.js, not here.
    world.events.on(EV.KILL, ({ unit, team }) => {
      if (unit.type !== UNIT_WARLORD || !unit.warlord) return;
      if (unit.warlord.ally) {
        world.events.emit(EV.ANNOUNCE, { text: `${unit.warlord.name} has fallen!`, tone: 'bad' });
      } else {
        if (unit !== world.dynast) world.warlordsSlain++;
        world.events.emit(EV.WARLORD_SLAIN, { warlord: unit, team });
      }
    });
  }

  step(input) {
    const world = this.world;
    const dt = TICK_DT;

    // spatial hash rebuild (positions at tick start)
    const hash = world.hash;
    hash.clear();
    const units = world.units.active;
    for (let i = 0; i < units.length; i++) hash.insert(units[i].x, units[i].z, units[i]._slot);

    // player
    const p = world.player;
    p.px = p.x; p.py = p.y; p.pz = p.z;
    if (!world.gameOver || world.won) updatePlayer(world, input, dt);

    // crowd AI (also saves each unit's prev position + runs reactions/projectiles)
    updateCrowd(world, dt);

    // combat continuations
    updateShockwaves(world, dt);
    updateTitanfall(world, dt);

    // battle direction
    updateSpawns(world, dt);
    updateWarlordEngagement(world);
    updateLevelScript(world);   // scripted cinematic beats (reinforcements, entrances, barks)

    // morale drifts back toward even so it reads as a tide, not a body count
    world.morale += (MORALE_START - world.morale) * MORALE_DRIFT * dt;

    // leaders regenerate slowly — light pressure won't fell them, but a genuine
    // collapse of their flank still can (the defeat/victory tension survives).
    regenLeader(world.dynast, dt);
    regenLeader(world.warmaster, dt);

    // combo decay + best-combo tracking
    if (world.combo > world.maxCombo) world.maxCombo = world.combo;
    if (world.combo > 0) {
      world.comboTimer += dt;
      if (world.comboTimer > HIT_COUNTER_DECAY) { world.combo = 0; world.comboTimer = 0; }
    }

    // mission progress / win-loss check (decides world.outcome)
    world.levelTime += dt;
    updateObjectives(world);

    world.simTime += dt;
  }
}

function regenLeader(u, dt) {
  if (u && u._active && u.state !== 7 /* ST_DEAD */ && u.hp < u.maxHp) {
    u.hp = Math.min(u.maxHp, u.hp + 9 * dt);
  }
}
